import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  createVirtualOfficeRoom,
  deleteVirtualOfficeRoom,
  listVirtualOfficeRooms,
  updateVirtualOfficeRoom,
} from "../../../api/client";
import { usePageContext } from "../../../context/PageContext";
import { useAuthContext } from "../../../context/AuthContext";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../../hooks/useEffectivePermissions";
import { usePositions } from "../../../hooks/usePositions";
import { useUsers } from "../../../hooks/useUsers";

function cargoLabel(p) {
  return String(p?.cargo || p?.nome || "").trim() || `Cargo #${p?.id}`;
}

function userLabel(u) {
  return String(u?.nome || u?.name || u?.email || "").trim() || `Usuário #${u?.id}`;
}

function toPositiveIdList(ids) {
  return Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => Number(id))
        .filter((n) => Number.isFinite(n) && n > 0)
    )
  );
}

export default function VirtualOfficeSettingsPage() {
  const { setCurrentPage } = usePageContext();
  React.useEffect(() => {
    setCurrentPage?.(1);
  }, [setCurrentPage]);

  const { userData } = useAuthContext();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permData } = useEffectivePermissions();
  const scopeSet = React.useMemo(
    () =>
      scopeSetFromPermissions(permData, {
        ...userData,
        master: isMasterUser ? 1 : 0,
      }),
    [permData, userData, isMasterUser]
  );
  const canManage = can(scopeSet, "virtual_office", "manage");

  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [selectedCargoIds, setSelectedCargoIds] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [error, setError] = useState("");

  const { data: positionsData, isLoading: positionsLoading } = usePositions(1, 500, "");
  const { data: usersData, isLoading: usersLoading } = useUsers(1, 1000, "", "humanos", {
    order: "nome",
  });

  const cargoOptions = useMemo(() => {
    const list = positionsData?.positions || [];
    return list
      .filter((p) => p && Number(p.id) > 0)
      .slice()
      .sort((a, b) => cargoLabel(a).localeCompare(cargoLabel(b), "pt-BR"));
  }, [positionsData]);

  const userOptions = useMemo(() => {
    const list = usersData?.users || [];
    return list
      .filter((u) => u && Number(u.id) > 0 && Number(u.excluido) !== 1)
      .slice()
      .sort((a, b) => userLabel(a).localeCompare(userLabel(b), "pt-BR"));
  }, [usersData]);

  const cargoById = useMemo(() => {
    const map = new Map();
    for (const p of cargoOptions) map.set(Number(p.id), p);
    return map;
  }, [cargoOptions]);

  const userById = useMemo(() => {
    const map = new Map();
    for (const u of userOptions) map.set(Number(u.id), u);
    return map;
  }, [userOptions]);

  const selectedCargos = useMemo(
    () =>
      selectedCargoIds.map(
        (id) => cargoById.get(Number(id)) || { id: Number(id), nome: `Cargo #${id}` }
      ),
    [selectedCargoIds, cargoById]
  );

  const selectedUsers = useMemo(
    () =>
      selectedUserIds.map(
        (id) => userById.get(Number(id)) || { id: Number(id), nome: `Usuário #${id}` }
      ),
    [selectedUserIds, userById]
  );

  const roomsQuery = useQuery(["settings-virtual-office-rooms"], async () => {
    const res = await listVirtualOfficeRooms();
    return res?.data?.data || res?.data || [];
  });

  const saveMutation = useMutation(
    async () => {
      const payload = {
        nome,
        descricao,
        ativo,
        allowedCargoIds: toPositiveIdList(selectedCargoIds),
        allowedUserIds: toPositiveIdList(selectedUserIds),
      };
      if (editing?.id) return updateVirtualOfficeRoom(editing.id, payload);
      return createVirtualOfficeRoom(payload);
    },
    {
      onSuccess: () => {
        setFormOpen(false);
        setEditing(null);
        qc.invalidateQueries(["settings-virtual-office-rooms"]);
      },
      onError: (e) => setError(e?.response?.data?.message || e?.message || "Erro ao salvar"),
    }
  );

  const deleteMutation = useMutation((id) => deleteVirtualOfficeRoom(id), {
    onSuccess: () => qc.invalidateQueries(["settings-virtual-office-rooms"]),
  });

  const rooms = useMemo(() => roomsQuery.data || [], [roomsQuery.data]);

  const openCreate = () => {
    setEditing(null);
    setNome("");
    setDescricao("");
    setAtivo(true);
    setSelectedCargoIds([]);
    setSelectedUserIds([]);
    setError("");
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setNome(row.nome || "");
    setDescricao(row.descricao || "");
    setAtivo(row.ativo !== false);
    setSelectedCargoIds(toPositiveIdList(row.allowedCargoIds));
    setSelectedUserIds(toPositiveIdList(row.allowedUserIds));
    setError("");
    setFormOpen(true);
  };

  const formatCargoNames = (ids) => {
    if (!ids?.length) return "todos";
    return ids
      .map((id) => {
        const p = cargoById.get(Number(id));
        return p ? cargoLabel(p) : `#${id}`;
      })
      .join(", ");
  };

  const formatUserNames = (ids) => {
    if (!ids?.length) return "todos";
    return ids
      .map((id) => {
        const u = userById.get(Number(id));
        return u ? userLabel(u) : `#${id}`;
      })
      .join(", ");
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Escritório Virtual
        </Typography>
        {canManage ? (
          <Button variant="contained" onClick={openCreate}>
            Nova sala
          </Button>
        ) : null}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure as salas (floors) do escritório virtual. Quem ingressa aparece na lista de
        presença. Ponto só é registrado para CLT com jornada, estágio e Outro com horário —
        prestador, função de confiança, Outro sem ponto e modalidade vazia entram sem controle
        de horário. Sem cargos nem usuários selecionados, qualquer
        pessoa com permissão de visualização pode ingressar; selecione para restringir o acesso.
      </Typography>

      {roomsQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Erro ao carregar salas.
        </Alert>
      ) : null}

      <Stack spacing={1.5}>
        {rooms.map((row) => (
          <Box
            key={row.id}
            sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 2 }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography fontWeight={600}>
                  {row.nome}{" "}
                  {!row.ativo ? (
                    <Typography component="span" variant="caption" color="text.secondary">
                      (inativa)
                    </Typography>
                  ) : null}
                </Typography>
                {row.descricao ? (
                  <Typography variant="body2" color="text.secondary">
                    {row.descricao}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Cargos: {formatCargoNames(row.allowedCargoIds)} · Usuários:{" "}
                  {formatUserNames(row.allowedUserIds)}
                </Typography>
              </Box>
              {canManage ? (
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => openEdit(row)}>
                    Editar
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      if (window.confirm("Remover esta sala?")) deleteMutation.mutate(row.id);
                    }}
                  >
                    Remover
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          </Box>
        ))}
        {!rooms.length && !roomsQuery.isLoading ? (
          <Alert severity="info">Nenhuma sala cadastrada.</Alert>
        ) : null}
      </Stack>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? "Editar sala" : "Nova sala"}</DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : null}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Descrição"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Autocomplete
              multiple
              filterSelectedOptions
              options={cargoOptions}
              value={selectedCargos}
              onChange={(_, value) =>
                setSelectedCargoIds(toPositiveIdList((value || []).map((p) => p?.id)))
              }
              getOptionLabel={cargoLabel}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              loading={positionsLoading}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={cargoLabel(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cargos permitidos"
                  helperText="Vazio = todos com permissão de visualização"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {positionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              multiple
              filterSelectedOptions
              options={userOptions}
              value={selectedUsers}
              onChange={(_, value) =>
                setSelectedUserIds(toPositiveIdList((value || []).map((u) => u?.id)))
              }
              getOptionLabel={userLabel}
              isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
              loading={usersLoading}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    label={userLabel(option)}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Usuários permitidos"
                  helperText="Vazio = todos com permissão de visualização"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {usersLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <FormControlLabel
              control={<Switch checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />}
              label="Sala ativa"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={!nome.trim() || saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
